import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiMinus, FiPlus, FiX } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { NumberFormatter } from '../../../shared/ui/number-formatter';
import { Item } from '../../../types/interfaces';
import { getCurrencySymbol } from '@shared/lib/currency';

export interface CartItemUIProps {
  item: Item;
  quantity: number;
  verificationQuantity: number;
  onQuantityChange: (value: number) => void;
  onVerificationQuantityChange: (value: number) => void;
  removeFromCart: () => void;
  removeVerifyHandler: (id: number) => void;
  addingItemToCart: (params: { quantity: number; verificationQuantity: number }) => void;
}


const QtyControl = ({
  value,
  onChange,
  min = 1,
  max = 10000,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) => (

  <div className="flex items-center gap-1.5">
    <button
      onClick={() => onChange(Math.max(min, value - 1))}
      disabled={value <= min}
      className="w-9 h-9 rounded-lg bg-[#F5F6FA] flex items-center justify-center text-[#141414] hover:text-[#e3282a] border-0 cursor-pointer disabled:opacity-30 shrink-0"
    >
      <FiMinus size={14} />
    </button>
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => {
        const v = parseInt(e.target.value, 10);
        // Пустой инпут (пользователь стёр всё) пропускаем —
        // конкретное значение применится по blur/Enter. Иначе
        // пробел между цифрами вернул бы NaN и сломал UI.
        if (Number.isFinite(v)) {
          onChange(Math.max(min, Math.min(max, v)));
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
      className="w-12 h-9 text-center text-sm font-medium tabular-nums text-[#141414] border border-gray-200 rounded-lg outline-none focus:border-[#e3282a] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
    />
    <button
      onClick={() => onChange(Math.min(max, value + 1))}
      disabled={value >= max}
      className="w-9 h-9 rounded-lg bg-[#F5F6FA] flex items-center justify-center text-[#141414] hover:text-[#e3282a] border-0 cursor-pointer disabled:opacity-30 shrink-0"
    >
      <FiPlus size={14} />
    </button>
  </div>
);

const AddSmallButton = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <button
    onClick={onClick}
    className="h-9 px-3 rounded-lg text-xs font-medium border border-[#e3282a] text-[#e3282a] bg-white hover:bg-[#e3282a] hover:text-white cursor-pointer transition-colors whitespace-nowrap"
  >
    {label}
  </button>
);

const ConfirmRemove = ({ onConfirm }: { onConfirm: () => void }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  // Поповер позиционируем через fixed-координаты кнопки и рендерим в
  // портал: родитель (CartSummary) имеет overflow-hidden, а строка —
  // overflow-x-auto, поэтому раньше absolute-окно подтверждения
  // обрезалось и казалось, что кнопка «не работает». Портал + fixed
  // выводит окно над блоком, вне clip-контекста.
  const [coords, setCoords] = useState<{ right: number; bottom: number } | null>(
    null
  );

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setCoords({
      right: window.innerWidth - rect.right,
      bottom: window.innerHeight - rect.top + 8,
    });
  }, [open]);

  // Скролл/ресайз делают сохранённые координаты неактуальными —
  // проще закрыть окно, чем пересчитывать на каждый кадр.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen(true)}
        className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-400 hover:text-[#e3282a] hover:bg-red-50 bg-transparent border-0 cursor-pointer transition-colors"
      >
        <FiX size={18} />
      </button>
      {open &&
        coords &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[1000]"
              onClick={() => setOpen(false)}
            />
            <div
              className="fixed z-[1001] bg-white rounded-xl shadow-lg border border-gray-100 p-3 w-48"
              style={{ right: coords.right, bottom: coords.bottom }}
            >
              <p className="text-xs text-gray-600 mb-2 font-medium">
                Удалить из корзины?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    onConfirm();
                    setOpen(false);
                  }}
                  className="flex-1 py-1 text-xs font-semibold text-white bg-[#e3282a] rounded-lg border-0 cursor-pointer hover:bg-[#c91f21]"
                >
                  Удалить
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="flex-1 py-1 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg border-0 cursor-pointer hover:bg-gray-200"
                >
                  Отмена
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export const CART_ROW_GRID_COLS =
  'minmax(0, 1fr) 160px 160px 100px 44px';

export const CartItemUI = ({
  item,
  quantity,
  verificationQuantity,
  onQuantityChange,
  onVerificationQuantityChange,
  removeFromCart,
  removeVerifyHandler,
  addingItemToCart,
}: CartItemUIProps): JSX.Element => {
 
  const currency = useSelector((state: any) => state.cart?.currency);
  const currencySymbol = getCurrencySymbol(currency || 'rub');

  const hasVerificationOption = !!item?.verification_item;

  // Потолок количества по складу (только KZ); RU — без лимита.
  const maxQuantity =
    item?.country === 'kz' && item?.available != null && Number(item.available) >= 1
      ? Number(item.available)
      : 10000;

  void removeVerifyHandler;

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div
        className="grid items-center gap-x-4 px-6 py-4"
        style={{ gridTemplateColumns: CART_ROW_GRID_COLS }}
      >
        <div className="text-sm text-[#141414] font-medium leading-snug">
          {item.name}
        </div>

        <QtyControl value={quantity} onChange={onQuantityChange} max={maxQuantity} />

        {hasVerificationOption ? (
          verificationQuantity > 0 ? (
            <QtyControl
              value={verificationQuantity}
              onChange={(v) => onVerificationQuantityChange(Math.min(v, quantity))}
              min={0}
            />
          ) : (
            <AddSmallButton
              onClick={() =>
                addingItemToCart({ quantity, verificationQuantity: quantity })
              }
              label="+ поверка"
            />
          )
        ) : (
          <span className="text-sm text-gray-300">—</span>
        )}

        <div className="text-base font-semibold text-[#141414] whitespace-nowrap">
          <NumberFormatter number={item?.totalPrice ? +item.totalPrice : 0} /> {currencySymbol}
        </div>

        <ConfirmRemove onConfirm={removeFromCart} />
      </div>

    </div>
  );
};
